import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import useStyles from "./style";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  APIProvider,
  Map,
  useMap,
  useMapsLibrary,
  AdvancedMarker,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { LocalTaxiRounded } from "@mui/icons-material";

import BookingForm from "../Dispatcherpanel/Bookingform";
import DispatcherApi from "Services/Dispatcher";
import { API_ENDPOINTS } from "../../Constants";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const Directions = memo(({ origin, destination }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary("routes");
  const [directionsRenderer, setDirectionsRenderer] = useState(null);

  useEffect(() => {
    if (!origin || !destination || !routesLibrary || !map) return undefined;

    // Première passe : créer le renderer et attendre le re-render
    if (!directionsRenderer) {
      setDirectionsRenderer(new routesLibrary.DirectionsRenderer({ map }));
      return undefined;
    }

    // Deuxième passe : le renderer existe, tracer le trajet
    const directionsService = new routesLibrary.DirectionsService();
    directionsService
      .route({ origin, destination, travelMode: "DRIVING" })
      .then((response) => directionsRenderer.setDirections(response))
      .catch((error) => console.error("Directions error:", error));

    return () => {
      // Cleanup uniquement sur la variable d'état (null-safe)
      directionsRenderer?.setMap(null);
    };
  }, [origin, destination, map, routesLibrary, directionsRenderer]);

  return null;
});

const DispatcherPassenger = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const timezone = useSelector((state) => {
    const tz = state?.timezone?.timezone || "America/Anchorage";
    return tz === "Asia/Coimbatore" ? "Asia/Kolkata" : tz;
  });
  const systemlatitude = useSelector((state) => Number(state?.timezone?.latitude) || 11.127123);
  const systemlongitude = useSelector((state) => Number(state?.timezone?.longitude) || 78.656891);

  const userId =
    useSelector((state) => state?.AuthReducer?.userid || "") ||
    localStorage.getItem("userid") ||
    "";
  const userType = useSelector((state) => state?.AuthReducer?.userType || "");
  const subcmpnyName = useSelector((state) => state?.AuthReducer?.subCmpny || "");

  const [services, setServices] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [infoWindowData, setInfoWindowData] = useState(null);
  const [open, setOpen] = useState(true);
  const [openbooking, setOpenbooking] = useState(true);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [mapCenter, setMapCenter] = useState({ lat: systemlatitude, lng: systemlongitude });

  const mapRef = useRef(null);
  const libraries = ["places", "drawing", "routes"];

  const loadServicesAndDrivers = useCallback(() => {
    dispatch(showLoader("Chargement..."));
    DispatcherApi.getdriversandservices()
      .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true) {
          setServices(res?.data?.data?.serviceTypeDetails || []);
          setAllDrivers(res?.data?.data?.driverInfoDetails || []);
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Erreur de chargement",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      });
  }, [dispatch]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat: systemlatitude, lng: systemlongitude });
    } else {
      setMapCenter({ lat: systemlatitude, lng: systemlongitude });
    }
    loadServicesAndDrivers();
  }, [systemlatitude, systemlongitude, loadServicesAndDrivers]);

  const handleMarkerClick = useCallback((driver) => {
    setInfoWindowData(driver);
  }, []);

  const driversToShow = allDrivers.filter((driver) => {
    const lat = Number(driver?.driver_ride_details?.latitude);
    const lng = Number(driver?.driver_ride_details?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng);
  });

  return (
    <Grid container className={classes.mapcontainer}>
      <Grid item xs={12}>
        <APIProvider apiKey={API_ENDPOINTS.GMAP_API} libraries={libraries}>
          <Map
            ref={mapRef}
            style={{ width: "111vw", height: "111vh" }}
            defaultCenter={mapCenter}
            defaultZoom={7}
            gestureHandling="greedy"
            mapId="40e581fce79a8421a2f62ee2"
            onLoad={(map) => (mapRef.current = map)}
          >
            {/* Formulaire de réservation passager */}
            <BookingForm
              userType={userType}
              subcmpnyName={subcmpnyName}
              userId={userId}
              setOpenbooking={setOpenbooking}
              setOpen={setOpen}
              openbooking={openbooking}
              setOrigin={setOrigin}
              setDestination={setDestination}
              origin={origin}
              destination={destination}
              setMapCenter={setMapCenter}
              services={services}
              timezone={timezone}
            />

            {/* Tracé de l'itinéraire */}
            <Directions origin={origin} destination={destination} />

            {/* Marqueurs conducteurs disponibles */}
            {driversToShow.map((driver, idx) => {
              const rideStatus = driver?.driver_ride_details?.rideStatus;
              const color =
                rideStatus === "ACTIVE" ? "#30e356"
                : rideStatus === "OFFLINE" ? "#f44336"
                : "#0472e2";
              return (
                <AdvancedMarker
                  key={driver?.id || driver?._id || idx}
                  position={{
                    lat: Number(driver.driver_ride_details.latitude),
                    lng: Number(driver.driver_ride_details.longitude),
                  }}
                  title="Driver Location"
                  onClick={() => handleMarkerClick(driver)}
                >
                  <Pin background={color} borderColor={color} glyphColor="#ffffff">
                    <LocalTaxiRounded style={{ background: color, borderRadius: "12px" }} />
                  </Pin>
                </AdvancedMarker>
              );
            })}

            {/* InfoWindow au clic sur un marqueur */}
            {infoWindowData && (
              <InfoWindow
                position={{
                  lat: Number(infoWindowData.driver_ride_details.latitude),
                  lng: Number(infoWindowData.driver_ride_details.longitude),
                }}
                onCloseClick={() => setInfoWindowData(null)}
              >
                <div style={{ padding: 10 }}>
                  <h3>Conducteur</h3>
                  <p><b>Nom :</b> {infoWindowData.firstName} {infoWindowData.lastName}</p>
                  <p><b>Service :</b> {infoWindowData?.driver_ride_details?.service_details?.serviceName}</p>
                  <p><b>Adresse :</b> {infoWindowData?.driver_ride_details?.address}</p>
                </div>
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </Grid>
    </Grid>
  );
};

export default DispatcherPassenger;
